import { faShuffle } from '@fortawesome/pro-regular-svg-icons';
import { RadioAvatarCard } from 'components/Form/RadioAvatarCard/RadioAvatarCard';
import { StaffRadioCard } from 'components/Staff/RadioCard/StaffRadioCard';
import { useTimeZone } from 'hooks/useTimeZone';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { dispatch } from 'store';
import { Staff } from 'types/Staff';
import { getOnlineBookingGroomingStaffNextAvailableDateThunk } from 'actions/onlineBooking/actions/OnlineBookingAvailabilityActions';
import { NEXT_AVAILABLE_DATE_WEEKS } from 'types/OnlineBooking';
import classes from './StaffSelector.module.scss';

interface StaffSelectorProps {
    preferredStaff?: Staff;
    staffs: Array<Staff>;
    onChange: (staff?: Staff) => void;
}

const DATE_FORMAT = 'MMM D, YYYY';

export const StaffSelector: React.FC<StaffSelectorProps> = ({
    staffs,
    preferredStaff,
    onChange
}) => {
    const timeZone = useTimeZone();

    const today = moment().tz(timeZone);

    // Local state management following the original thunk logic
    const [isLoading, setIsLoading] = React.useState(
        new Map(staffs.map(staff => [staff.id, true] as [number, boolean]))
    );
    const [isBusy, setIsBusy] = React.useState(
        new Map(staffs.map(staff => [staff.id, false] as [number, boolean]))
    );
    const [isFetched, setIsFetched] = React.useState(new Set<number>());
    const [nextAvailableDates, setNextAvailableDates] = React.useState(new Map<number, Moment>());

    // Implementation following fetchOnlineBookingStaffNextAvailableDateThunk logic
    const checkStaffAvailability = React.useCallback(
        async (date: Moment, staffIdsToCheck: Array<number>) => {
            if (staffIdsToCheck.length === 0) return;

            try {
                // Step 1: Filter to only available staff IDs (staffs that can perform selected services)
                const availableStaffIds = staffs.map(staff => staff.id);

                // Step 2: Set loading state for staffs that are being checked
                setIsLoading(prev => {
                    const newMap = new Map(prev);
                    availableStaffIds
                        .filter(staffId => staffIdsToCheck.includes(staffId))
                        .forEach(staffId => newMap.set(staffId, true));
                    return newMap;
                });

                // Step 3: Fetch availability for given staffs
                const { nextDate } = await dispatch(
                    getOnlineBookingGroomingStaffNextAvailableDateThunk(
                        date,
                        NEXT_AVAILABLE_DATE_WEEKS,
                        staffIdsToCheck
                    )
                );

                // Step 4: Process results - get staff IDs with availability from hours
                const staffIdsWithAvailability = Array.from(
                    new Set(nextDate.hours.flatMap(hour => hour.staffAvailable))
                );

                // Step 5: Identify non-available staffs (same logic as original)
                const nonAvailableStaffs = availableStaffIds
                    .filter(id => isFetched.has(id)) // Check if staff is already fetched
                    .filter(id => staffIdsToCheck.includes(id)) // Check if staff is in current batch
                    .filter(id => !staffIdsWithAvailability.includes(id)); // Check if staff is not available

                // Step 6: Set next available date for staffs that have availability (IMMEDIATE)
                if (staffIdsWithAvailability.length > 0) {
                    setNextAvailableDates(prev => {
                        const newMap = new Map(prev);
                        staffIdsWithAvailability.forEach(staffId => {
                            if (!newMap.has(staffId)) {
                                newMap.set(staffId, nextDate.date.clone());
                            }
                        });
                        return newMap;
                    });

                    // Set loading to false IMMEDIATELY for staffs that are found available
                    setIsLoading(prev => {
                        const newMap = new Map(prev);
                        staffIdsWithAvailability.forEach(staffId => {
                            newMap.set(staffId, false);
                        });
                        return newMap;
                    });
                }

                // Step 7: Set staffs as fetched
                setIsFetched(prev => {
                    const newSet = new Set(prev);
                    staffIdsToCheck.forEach(staffId => newSet.add(staffId));
                    return newSet;
                });

                // Step 8: Set staffs as busy (IMMEDIATE for non-available staffs)
                if (nonAvailableStaffs.length > 0) {
                    setIsBusy(prev => {
                        const newMap = new Map(prev);
                        nonAvailableStaffs.forEach(staffId => newMap.set(staffId, true));
                        return newMap;
                    });

                    // Set loading to false IMMEDIATELY for staffs that are busy
                    setIsLoading(prev => {
                        const newMap = new Map(prev);
                        nonAvailableStaffs.forEach(staffId => newMap.set(staffId, false));
                        return newMap;
                    });
                }

                // Step 9: Find next staffs to be checked (same logic as original)
                const remainingStaffIds = availableStaffIds.filter(
                    staffId =>
                        !staffIdsWithAvailability.includes(staffId) &&
                        staffIdsToCheck.includes(staffId)
                );

                // Step 10: Recursive logic (same as original)
                if (nextDate.hours.length > 0 && remainingStaffIds.length > 0) {
                    // Take the first available hour to check next
                    const nextCheckDate = nextDate.hours[0].datetime;
                    await checkStaffAvailability(nextCheckDate, remainingStaffIds);
                }

                // Step 11: If all staffs are busy on first fetch, set them as busy
                if (
                    remainingStaffIds.length === staffIdsToCheck.length &&
                    nextDate.hours.length === 0
                ) {
                    setIsBusy(prev => {
                        const newMap = new Map(prev);
                        staffIdsToCheck.forEach(staffId => newMap.set(staffId, true));
                        return newMap;
                    });

                    // Set loading to false for all staffs that are marked as busy
                    setIsLoading(prev => {
                        const newMap = new Map(prev);
                        staffIdsToCheck.forEach(staffId => newMap.set(staffId, false));
                        return newMap;
                    });
                }
            } catch (error) {
                console.error('Error checking staff availability:', error);
                // On error, set loading to false but don't mark as busy
                setIsLoading(prev => {
                    const newMap = new Map(prev);
                    staffIdsToCheck.forEach(staffId => newMap.set(staffId, false));
                    return newMap;
                });
            }
        },
        [staffs, isFetched]
    );

    React.useEffect(() => {
        // Start checking availability for all staffs
        const allStaffIds = staffs.map(staff => staff.id);
        checkStaffAvailability(today, allStaffIds);
        // eslint-disable-next-line
    }, []);

    // Generate description for "no preference" option - show earliest available date from any staff
    const nextAvailableDateForNoPreference = React.useMemo(() => {
        const availableDates = Array.from(nextAvailableDates.values());
        if (availableDates.length === 0) {
            return ' ';
        }

        // Find the earliest date
        const earliestDate = availableDates.reduce((earliest, current) => {
            return current.isBefore(earliest) ? current : earliest;
        });

        return (
            <>
                Available: <b>{earliestDate.format(DATE_FORMAT)}</b>
            </>
        );
    }, [nextAvailableDates]);

    return (
        <div className={classes.root}>
            <RadioAvatarCard
                icon={faShuffle}
                label="No preference"
                checked={preferredStaff === undefined}
                description={nextAvailableDateForNoPreference}
                onChange={() => onChange(undefined)}
            />

            {staffs.map(staff => (
                <StaffRadioCard
                    key={staff.id}
                    staff={staff}
                    loading={isLoading.get(staff.id)}
                    dateFormat={DATE_FORMAT}
                    busyForWeeks={isBusy.get(staff.id) ? NEXT_AVAILABLE_DATE_WEEKS : undefined}
                    availableAt={nextAvailableDates.get(staff.id)}
                    selected={preferredStaff?.id === staff.id}
                    onSelected={() => onChange(staff)}
                />
            ))}
        </div>
    );
};
